import React from 'react';

// Props for the CombatLog component. We accept a list of log lines and
// render only the top few. Memoizing based on these visible lines prevents
// repeated re-renders of the log when Board state updates unrelated to
// logging occur.
export type CombatLogProps = {
  lines: string[];
};

/**
 * Compare only the visible portion of the log (up to 12 lines). If the
 * currently displayed lines are identical between renders, React.memo can
 * skip updating this component. This avoids unnecessary DOM churn when
 * Board or other parent components update frequently during combat.
 */
function areVisibleLogLinesEqual(prevLines: string[], nextLines: string[]) {
  const prevCount = Math.min(prevLines.length, 12);
  const nextCount = Math.min(nextLines.length, 12);
  if (prevCount !== nextCount) return false;

  for (let i = 0; i < prevCount; i += 1) {
    if (prevLines[i] !== nextLines[i]) return false;
  }

  return true;
}

/**
 * Top-level comparator for CombatLog props. Delegates to the visible
 * line comparison function so that only changes to the first few log
 * entries cause a re-render.
 */
function areCombatLogPropsEqual(prev: CombatLogProps, next: CombatLogProps) {
  return areVisibleLogLinesEqual(prev.lines, next.lines);
}

/**
 * A compact log display for combat. Shows up to the most recent 12 lines.
 * Uses a memoized slice of the provided lines array to avoid re-slicing on
 * every parent render.
 */
function CombatLog({ lines }: CombatLogProps) {
  const visibleLines = React.useMemo(() => lines.slice(0, 12), [lines]);

  return (
    <div className="log">
      <div className="logTitle">Log</div>
      <div className="logBody">
        {visibleLines.map((l, i) => (
          <div key={i} className="logLine">{l}</div>
        ))}
      </div>
    </div>
  );
}

export default React.memo(CombatLog, areCombatLogPropsEqual);